import { OrderProduct } from '../types/order';

/** Normalize CRM codes and order subcategory slugs for comparison. */
export function normalizeServiceTypeToken(s: string | null | undefined): string {
  return (s || '').trim().toLowerCase().replace(/-/g, '_');
}

/**
 * Groups of equivalent tokens: CRM `service_types.code`, `order_products.service_type_code`,
 * and `order_subcategories.slug` (after normalization) may use any member of a group.
 */
const SERVICE_TYPE_SYNONYM_GROUPS: string[][] = [
  ['nbn'],
  ['supa_networks', 'supa'],
  ['mobile'],
  ['voice', 'voice_pbx', 'voice_pbx_system', 'pbx'],
  ['sonweb', 'sonic_web', 'sonic_web_hosting', 'web_hosting', 'hosting'],
  ['data'],
  ['virtual_machine', 'vm'],
];

function synonymTokenSetForFilter(filterCode: string): Set<string> {
  const n = normalizeServiceTypeToken(filterCode);
  const out = new Set<string>([n]);
  if (!n) return out;
  for (const g of SERVICE_TYPE_SYNONYM_GROUPS) {
    const gn = g.map(normalizeServiceTypeToken);
    if (gn.includes(n)) {
      gn.forEach((x) => out.add(x));
    }
  }
  return out;
}

function slugMatchesAnyToken(slug: string, tokens: Set<string>): boolean {
  const s = normalizeServiceTypeToken(slug);
  if (!s) return false;
  for (const t of Array.from(tokens)) {
    if (!t) continue;
    if (s === t) return true;
    if (s.startsWith(`${t}_`) || s.startsWith(`${t}.`)) return true;
  }
  return false;
}

/**
 * True when the CRM service-type filter should include this catalog product.
 * Matches `service_type_code` and/or order subcategory slug (products often only set the latter).
 */
export function orderProductMatchesServiceTypeFilter(
  product: OrderProduct,
  filterCode: string,
  subcategorySlugById: Record<string, string>,
): boolean {
  const fc = normalizeServiceTypeToken(filterCode);
  if (!fc) return true;
  const tokens = synonymTokenSetForFilter(filterCode);
  const svc = normalizeServiceTypeToken(product.service_type_code);
  if (svc && tokens.has(svc)) return true;
  const rawSlug = product.subcategory_id ? subcategorySlugById[product.subcategory_id] : '';
  const slug = normalizeServiceTypeToken(rawSlug);
  if (slug && tokens.has(slug)) return true;
  if (rawSlug && slugMatchesAnyToken(rawSlug, tokens)) return true;
  return false;
}
